/**
 * @copyright 2023 Honda Motor Co., Ltd. All rights reserved.
 */

import {
  useState,
  FC,
  useEffect,
  useLayoutEffect,
  useCallback,
  useRef,
} from "react";
import {
  MenuItem,
  Typography,
  Grid,
  ToggleButton,
  ToggleButtonGroup,
  Select,
  Button,
  Box,
} from "@mui/material";
import { SelectChangeEvent } from "@mui/material/Select";
import type { PlanJobInfoVehicleBase } from "client/models/PlanJobInfoVehicleBase";
import { ReactComponent as PointIcon } from "assets/point_icon.svg";
import { TaskData, useTaskDataContext } from "context/TaskDataProvider";
import { PanelContentsContainer } from "./Util";
import { MapNodeType } from "client";
import { useTranslation } from "react-i18next";
import { TaskControlState, PHASE_SELECT_POINT } from "./consts";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { PointSwapper } from "./PointSwapper";
import { getNodeType } from "components/Map/NodeMapOverlay";

/**
 * Point node
 */
export interface PointSource {
  /** Key */
  key: number;
  /** Node ID */
  nodeId: number | undefined;
  /** Description */
  label: string | undefined;
}

export interface PassPointInfo {
  key: number;
  nodeId: number | undefined;
  startType: StartType;
  departAfter: number;
}

type StartType = "auto" | "manual";

const defaultPassPoints = Array<PassPointInfo>(2)
  .fill({
    key: 0,
    nodeId: undefined,
    startType: "manual",
    departAfter: 15,
  })
  .map((arr, i) => {
    arr.key = i;
    return arr;
  });

const TIME_RANGE = Array<number>(90 / 5)
  .fill(5)
  .map((n, i) => n * i + 5);

interface SelectPointPanelProps {
  state: TaskControlState;
  pointSourceList: PointSource[];
  selectedMapNode: MapNodeType | undefined;
  onPassPointsChanged?: (passPoints: PassPointInfo[]) => void;
}

interface SelectPointProps {
  listData: Array<PassPointInfo>;
  pointSource: Array<PointSource>;
  setListData: React.Dispatch<React.SetStateAction<PassPointInfo[]>>;
}

export const SelectPointPanel: FC<SelectPointPanelProps> = ({
  state,
  pointSourceList,
  selectedMapNode,
  onPassPointsChanged,
}) => {
  const { taskData, setTaskData } = useTaskDataContext();
  const [passPoints, setPassPoints] = useState(defaultPassPoints);

  const taskDataRef = useRef(taskData);

  useLayoutEffect(() => {
    taskDataRef.current = taskData;
  }, [taskData]);

  useEffect(() => {
    if (taskData.jobSetId !== "") {
      const items = getTaskDateRoot(taskDataRef.current);
      setPassPoints(items);
    }
  }, [taskData.jobSetId]);

  useEffect(() => {
    if (
      passPoints.length > 0 &&
      passPoints.every((point) => point.nodeId !== undefined)
    ) {
      const jobInfos: Array<PlanJobInfoVehicleBase> = passPoints
        .map((point) => {
          return {
            unloadingNodeId: point.nodeId!,
            extensionTimeUnload: getExtensionTimeUnload(point),
          };
        })
        .slice(1, -1); // exclude start and end node.
      const toIdx = passPoints.length - 1;

      setTaskData((pre) => {
        return {
          ...pre,
          startNodeId: passPoints[0].nodeId,
          extensionTimeLoad:
            passPoints[0].startType === "auto"
              ? passPoints[0].departAfter
              : undefined,
          endNodeId: passPoints[toIdx].nodeId,
          jobInfos: jobInfos,
        };
      });
    }
    onPassPointsChanged?.(passPoints);
  }, [onPassPointsChanged, passPoints, setTaskData]);

  useEffect(() => {
    if (
      state.process[state.phase] === PHASE_SELECT_POINT &&
      selectedMapNode !== undefined
    ) {
      setPassPoints((pre) => {
        const items: Array<PassPointInfo> = [];
        let flag = true;
        pre.forEach((passPoint) => {
          if (passPoint.nodeId === undefined && flag) {
            if (
              selectedMapNode.id !== undefined &&
              getNodeType(selectedMapNode.description).type === "NamedNode"
            ) {
              passPoint.nodeId = selectedMapNode.id;
            }
            flag = false;
          }
          items.push(passPoint);
        });
        return items;
      });
    }
  }, [selectedMapNode, state.phase, state.process]);

  const getExtensionTimeUnload = (data: PassPointInfo): number | undefined => {
    return undefined;
  };

  const getTaskDateRoot = (data: TaskData): Array<PassPointInfo> => {
    const items: Array<PassPointInfo> = [];
    let cnt = 1;
    const from: PassPointInfo = {
      key: 0,
      nodeId: data.startNodeId,
      startType: "manual",
      departAfter: data.extensionTimeLoad ?? 15,
    };
    items.push(from);
    if (data.jobInfos) {
      data.jobInfos.forEach((wayPoint) => {
        const wayPointInfo: PassPointInfo = {
          key: cnt,
          nodeId: wayPoint.unloadingNodeId,
          startType: "manual",
          departAfter: wayPoint.extensionTimeUnload ?? 15,
        };
        items.push(wayPointInfo);
        cnt++;
      });
    }
    const to: PassPointInfo = {
      key: cnt,
      nodeId: data.endNodeId,
      startType: "manual",
      departAfter: 15,
    };
    items.push(to);

    return items;
  };

  return (
    <>
      <PanelContentsContainer>
        <ListSelection
          listData={passPoints}
          pointSource={pointSourceList}
          setListData={setPassPoints}
        />
      </PanelContentsContainer>
    </>
  );
};

const ListSelection: FC<SelectPointProps> = ({
  listData,
  pointSource,
  setListData,
}) => {
  const { t } = useTranslation();
  const selectRef = useRef<Array<any>>([]);

  const handleSelectItem = useCallback(
    (event: SelectChangeEvent<number | undefined>, index: number) => {
      const { value } = event.target;
      setListData((pre) => {
        const updatedListData = [...pre];
        const parsedValue =
          typeof value === "string" ? parseInt(value, 10) : value;
        updatedListData[index].nodeId = parsedValue;
        return updatedListData;
      });
    },
    [setListData]
  );

  const handleSelectTime = useCallback(
    (event: SelectChangeEvent<number>, index: number) => {
      const { value } = event.target;
      setListData((pre) => {
        const updatedListData = [...pre];
        const parsedValue =
          typeof value === "string" ? parseInt(value, 10) : value;
        updatedListData[index].departAfter = parsedValue;
        return updatedListData;
      });
    },
    [setListData]
  );

  const handleSelectToggle = useCallback(
    (
      event: React.MouseEvent<HTMLElement, MouseEvent>,
      newStartType: StartType,
      index: number
    ) => {
      if (newStartType) {
        setListData((pre) => {
          const updatedListData = [...pre];
          updatedListData[index].startType = newStartType;
          return updatedListData;
        });
      }
    },
    [setListData]
  );

  const handleItemDelete = useCallback(
    (event: React.MouseEvent<HTMLButtonElement, MouseEvent>, index: number) => {
      setListData((pre) => {
        const updatedList = [];
        for (let i = 0; i < pre.length; i++) {
          if (i !== index) {
            updatedList.push(pre[i]);
          }
        }
        return updatedList;
      });
    },
    [setListData]
  );

  const handleAddItem = useCallback(() => {
    setListData((pre) => {
      const newKey = getMaxKey(pre) + 1;
      const item: PassPointInfo = {
        key: newKey,
        nodeId: undefined,
        startType: "manual",
        departAfter: 15,
      };
      return [...pre, item];
    });
  }, [setListData]);

  const getMaxKey = (array: PassPointInfo[]): number => {
    if (array.length === 0) {
      return 0;
    }
    let maxKey = array[0].key;
    for (let i = 1; i < array.length; i++) {
      if (array[i].key > maxKey) {
        maxKey = array[i].key;
      }
    }
    return maxKey;
  };

  const handleItemMove = useCallback(
    (direction: string, index: number) => {
      const newIndex = direction === "up" ? index - 1 : index + 1;

      setListData((pre) => {
        if (newIndex < 0 || newIndex === pre.length) {
          return pre;
        }
        const newList = [...pre];
        const [removed] = newList.splice(index, 1);
        newList.splice(newIndex, 0, removed);
        return newList;
      });
    },
    [setListData]
  );

  const getDisableExchange = (
    dataList: PassPointInfo[],
    index: number
  ): boolean => {
    if (index > dataList.length - 1) {
      return true;
    }
    if (
      dataList[index].nodeId === undefined ||
      dataList[index + 1].nodeId === undefined
    ) {
      return true;
    }
    return false;
  };

  // get select box highlight
  useEffect(() => {
    const getAutoFocus = (index: number): boolean => {
      if (listData[index].nodeId !== undefined) {
        return false;
      } else {
        for (let i = 0; i < index; i++) {
          if (listData[i].nodeId === undefined) {
            return false;
          }
        }
      }
      return true;
    };
    selectRef.current.forEach((input, index) => {
      // If autoFocus is true, then soft keyboard is popup. so use focus() function.
      if (input && getAutoFocus(index)) {
        input.focus();
      }
    });
  }, [listData]);

  return (
    <>
      {listData.map((item: PassPointInfo, index: number) => (
        <div key={`point-list-${index}`}>
          {index === 0 ? (
            <Grid container alignItems="center" spacing={1}>
              <Grid item xs={2}>
                <Typography variant="h5"></Typography>
              </Grid>
              <Grid item xs={3}>
                <Typography variant="subtitle1">{t("AWVUI-MSG37")}</Typography>
              </Grid>
              <Grid item xs={4}></Grid>
              <Grid item xs={3}>
                <Typography variant="subtitle1">{t("AWVUI-MSG53")}</Typography>
              </Grid>
            </Grid>
          ) : (
            <></>
          )}
          <Grid container alignItems="center" spacing={1}>
            <Grid item xs={2}>
              {index !== 0 && listData.length > 2 && (
                <Button
                  onClick={(e) => handleItemDelete(e, index)}
                  color="inherit"
                  variant="outlined"
                  sx={{
                    width: "2.5rem",
                    height: "2.5rem",
                    minWidth: "2.5rem",
                  }}
                >
                  <RemoveIcon />
                </Button>
              )}
            </Grid>
            <Grid item xs={7}>
              <Select
                sx={{ width: "100%", backgroundColor: "white" }}
                size="small"
                value={item.nodeId ?? ""}
                onChange={(e) => handleSelectItem(e, index)}
                inputRef={(input) => {
                  selectRef.current[index] = input;
                }}
              >
                {pointSource.map((point: PointSource) => (
                  <MenuItem key={point.key} value={point.nodeId}>
                    <Grid container alignItems="center" spacing={1}>
                      <Grid item xs={2}>
                        <PointIcon />
                      </Grid>
                      <Grid item xs={3}>
                        {point.label}
                      </Grid>
                    </Grid>
                  </MenuItem>
                ))}
              </Select>
            </Grid>
            <Grid item xs={3}>
              {index !== listData.length - 1 && (
                <Select
                  sx={{ width: "100%", backgroundColor: "white" }}
                  size="small"
                  value={
                    TIME_RANGE.includes(item.departAfter)
                      ? item.departAfter
                      : 15
                  }
                  onChange={(e) => handleSelectTime(e, index)}
                  disabled={item.startType === "manual"}
                >
                  {TIME_RANGE.map((time) => (
                    <MenuItem key={time} value={time}>
                      {time}
                    </MenuItem>
                  ))}
                </Select>
              )}
            </Grid>
          </Grid>
          {index !== listData.length - 1 && (
            <Grid container alignItems="center" spacing={1}>
              <Grid item xs={2}>
                <Typography variant="h5"></Typography>
              </Grid>
              <Grid item xs={10}>
                <ToggleButtonGroup
                  color="primary"
                  value={item.startType}
                  exclusive
                  onChange={(e, data) => handleSelectToggle(e, data, index)}
                  aria-label="Platform"
                  sx={{ width: "100%" }}
                  disabled
                >
                  <ToggleButton
                    value="manual"
                    sx={{ width: "100%", color: "black" }}
                  >
                    {t("AWVUI-MSG55")}
                  </ToggleButton>
                  <ToggleButton
                    value="auto"
                    sx={{ width: "100%", color: "black" }}
                  >
                    {t("AWVUI-MSG56")}
                  </ToggleButton>
                </ToggleButtonGroup>
              </Grid>
            </Grid>
          )}
          {index !== listData.length - 1 && (
            <Grid container alignItems="flex-end" spacing={0}>
              <Grid item xs={2}>
                <Typography variant="h5"></Typography>
              </Grid>
              {index === listData.length - 2 ? (
                <Grid item xs={3}>
                  <Typography variant="subtitle1">
                    {t("AWVUI-MSG38")}
                  </Typography>
                </Grid>
              ) : (
                <Grid item xs={3}></Grid>
              )}
              <Grid item xs={7}>
                <Box sx={{ paddingTop: "0px" }}>
                  <PointSwapper
                    onClick={() => handleItemMove("down", index)}
                    disabled={getDisableExchange(listData, index)}
                  />
                </Box>
              </Grid>
            </Grid>
          )}
        </div>
      ))}
      <Box
        sx={{
          display: "flex",
          width: "100%",
          justifyContent: "center",
          paddingTop: 2,
        }}
      >
        <Button
          onClick={handleAddItem}
          color="inherit"
          variant="outlined"
          disabled={listData.length >= 100}
          sx={{ width: "140px", height: "3rem" }}
        >
          <AddIcon />
        </Button>
      </Box>
    </>
  );
};
